import { useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import { toast, Toaster } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full bg-transparent border-b border-[#F7F5F0]/25 py-3 text-[#F7F5F0] caret-[#F7F5F0] placeholder:text-[#F7F5F0]/45 focus:outline-none focus:border-[#F7F5F0] transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Your enquiry has been received.");
      setForm({ name: "", email: "", company: "", role: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#111111] text-[#F7F5F0] py-24 sm:py-32 lg:py-44 relative film-grain"
    >
      <Toaster position="top-center" theme="dark" />
      <div className="max-w-7xl mx-auto px-6 sm:px-12 relative z-[2]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline text-[#C9BCA8] mb-6">Enquire</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                Let us form your next listing.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-[#F7F5F0]/60 leading-relaxed max-w-md">
                Tell us about the property and the photography you hold. We reply
                to every enquiry within one working day.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-12 space-y-4 text-sm">
                <p className="text-[#F7F5F0]/50 tracking-wide uppercase text-xs">
                  Studio
                </p>
                <p className="text-[#F7F5F0]/80">hello@formpathway.com</p>
                <p className="text-[#F7F5F0]/80">London · Worldwide</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              {sent ? (
                <div
                  data-testid="contact-success"
                  className="border border-[#F7F5F0]/20 p-12 lg:p-16 h-full flex flex-col justify-center"
                >
                  <p className="overline text-[#C9BCA8] mb-5">Received</p>
                  <h3 className="font-display font-light text-3xl sm:text-4xl leading-snug">
                    Thank you. Your enquiry is with our studio.
                  </h3>
                  <p className="mt-5 text-[#F7F5F0]/60">
                    We will be in touch shortly to discuss your film.
                  </p>
                  <button
                    data-testid="contact-reset"
                    onClick={() => setSent(false)}
                    className="mt-10 self-start text-xs tracking-[0.18em] uppercase border-b border-[#F7F5F0]/40 pb-1 hover:border-[#F7F5F0] transition-colors"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="contact-form"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-9"
                >
                  <input
                    data-testid="contact-name"
                    className={fieldClass}
                    placeholder="Full name *"
                    value={form.name}
                    onChange={set("name")}
                  />
                  <input
                    data-testid="contact-email"
                    type="email"
                    className={fieldClass}
                    placeholder="Email address *"
                    value={form.email}
                    onChange={set("email")}
                  />
                  <input
                    data-testid="contact-company"
                    className={fieldClass}
                    placeholder="Company / Agency"
                    value={form.company}
                    onChange={set("company")}
                  />
                  <input
                    data-testid="contact-role"
                    className={fieldClass}
                    placeholder="Your role"
                    value={form.role}
                    onChange={set("role")}
                  />
                  <textarea
                    data-testid="contact-message"
                    rows={4}
                    className={`${fieldClass} sm:col-span-2 resize-none`}
                    placeholder="Tell us about the property *"
                    value={form.message}
                    onChange={set("message")}
                  />
                  <div className="sm:col-span-2">
                    <button
                      data-testid="contact-submit"
                      type="submit"
                      disabled={loading}
                      className="group inline-flex items-center gap-3 bg-[#F7F5F0] text-[#1A1A1A] px-10 py-4 text-xs tracking-[0.18em] uppercase hover:bg-accent hover:text-[#F7F5F0] transition-all duration-300 disabled:opacity-60"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ArrowRight
                          strokeWidth={1.25}
                          className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300"
                        />
                      )}
                      Submit Enquiry
                    </button>
                  </div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
